import * as config from "config";
import { useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";

/**
 * A layer supports a retroactive date when EITHER its selected policy type
 * (from the `policy_types` config, `choices[].hasRetroactiveDate`) OR its
 * selected policy form (from the `policy_forms` config,
 * `policy_forms[].hasRetroactiveDate`) is flagged as having one.
 */
export const hasRetroactiveDateSupport = ({
  policyTypeChoices,
  policyForms,
  policyType,
  policyForm,
}) => {
  const typeChoice = (policyTypeChoices ?? []).find(
    (choice) => choice?.key === policyType
  );
  if (typeChoice?.hasRetroactiveDate) {
    return true;
  }
  const formChoice = (policyForms ?? []).find(
    (form) => form?.code === policyForm
  );
  return !!formChoice?.hasRetroactiveDate;
};

export const usePolicyForms = () => {
  const { data, isLoading } = staticDataService.useConfigQuery("policy_forms");
  const policyForms = Array.isArray(data?.policy_forms)
    ? data.policy_forms
    : config.POLICY_FORMS;
  return { policyForms, isLoading };
};

/**
 * Returns `supports(policyType, policyForm)` bound to the current company's
 * config, plus `supported` for the active layer's current selection.
 */
export const useRetroactiveDateSupport = () => {
  const { data: policyTypeConfig } = staticDataService.useConfigQuery(
    "policy_types"
  );
  const { policyForms } = usePolicyForms();
  const layer = useSelector(pricingSelectors.selectResolvedActiveLayer);

  const supports = (policyType, policyForm) =>
    hasRetroactiveDateSupport({
      policyTypeChoices: policyTypeConfig?.choices,
      policyForms,
      policyType,
      policyForm,
    });

  return {
    supports,
    supported: supports(layer?.policyType, layer?.policyForm),
    layer,
  };
};
